/**
 * Wire types shared by the Vortex server and web client.
 *
 * Imported by the server as a relative path and by the web client through the
 * `@shared` Vite alias, so there is exactly one definition of each message.
 */

/** A room code as stored/compared: 16 Crockford base32 chars, no separators. */
export type RoomCode = string;

/**
 * What a meeting is for, which is enough to settle every capture and encoding
 * decision that follows.
 *
 * A named preset rather than a panel of knobs: the settings are not
 * independent. Stereo is the clearest case — Chrome's audio pipeline downmixes
 * to mono whenever echo cancellation is on, so "stereo" and "no echo
 * cancellation" are one decision, not two, and offering them separately would
 * let a host pick a combination that cannot exist.
 *
 * The values are stored in meetings.json, so they are names rather than
 * numbers: retuning a preset later should change existing meetings, which is
 * the point of them being presets.
 */
export type MediaProfile = 'low' | 'meeting' | 'hq-video' | 'studio';

export const MEDIA_PROFILES: MediaProfile[] = ['low', 'meeting', 'hq-video', 'studio'];

export const DEFAULT_MEDIA: MediaProfile = 'meeting';

/** Host-facing copy. Kept beside the type so a new preset cannot ship unlabelled. */
export const MEDIA_PROFILE_LABELS: Record<MediaProfile, { name: string; detail: string }> = {
  low: {
    name: 'Low bandwidth',
    detail:
      'Holds a call together on a phone tether or a hotel connection. 360p, and audio given priority over picture.',
  },
  meeting: {
    name: 'Standard meeting',
    detail: '720p, with echo cancellation and noise suppression. The right choice for talking to people.',
  },
  'hq-video': {
    name: 'HQ video',
    detail:
      '1080p at around double the bitrate, for when the camera is pointed at something worth resolving. Voice audio, unchanged.',
  },
  studio: {
    name: 'HQ video + stereo sound',
    detail:
      '1080p and full-bandwidth stereo, with the browser’s voice processing switched off so music survives. Everyone needs headphones — without echo cancellation, open speakers will feed back.',
  },
};

export function isMediaProfile(v: unknown): v is MediaProfile {
  return typeof v === 'string' && (MEDIA_PROFILES as string[]).includes(v);
}

/** Public view of a meeting — safe to hand to anyone holding the link. */
export interface MeetingPublic {
  code: RoomCode;
  title: string;
  /** ISO timestamp. */
  createdAt: string;
  /** ISO timestamp, or null when the meeting does not expire. */
  expiresAt: string | null;
  /** Whether the host requires participants to wait for admission. */
  lobbyEnabled: boolean;
  /** Chosen by the host at creation. Participants need it before they capture,
      so it travels with the public lookup rather than with the join. */
  media: MediaProfile;
}

/** Host's view — adds the things only the creator should see. */
export interface MeetingHostView extends MeetingPublic {
  joinUrl: string;
  participantCount: number;
}

export interface Participant {
  id: string;
  name: string;
  isHost: boolean;
  /** Publishing state, mirrored from the client so peers can render placeholders. */
  audioEnabled: boolean;
  videoEnabled: boolean;
  screenSharing: boolean;
  joinedAt: string;
}

/* ------------------------------------------------------------------ */
/* Client -> server                                                    */
/* ------------------------------------------------------------------ */

export type ClientMessage =
  | { t: 'join'; code: RoomCode; name: string; audioEnabled: boolean; videoEnabled: boolean }
  | { t: 'leave' }
  | { t: 'state'; audioEnabled?: boolean; videoEnabled?: boolean; screenSharing?: boolean }
  | { t: 'chat'; text: string }
  | { t: 'ping' };

/* ------------------------------------------------------------------ */
/* Server -> client                                                    */
/* ------------------------------------------------------------------ */

/**
 * Everything the client needs to reach the SFU. Issued only after the room code
 * has been checked, so the code stays the single gate — there is no separate
 * token endpoint to guess at or rate-limit.
 */
export interface RtcCredentials {
  url: string;
  token: string;
}

export type ServerMessage =
  | {
      t: 'joined';
      self: Participant;
      meeting: MeetingPublic;
      participants: Participant[];
      rtc: RtcCredentials | null;
    }
  | { t: 'peer-joined'; participant: Participant }
  | { t: 'peer-left'; id: string }
  | { t: 'peer-state'; id: string; audioEnabled: boolean; videoEnabled: boolean; screenSharing: boolean }
  | { t: 'chat'; from: string; name: string; text: string; at: string }
  | { t: 'pong' }
  | { t: 'error'; code: JoinErrorCode; message: string };

export type JoinErrorCode =
  | 'not-found'
  | 'expired'
  | 'full'
  | 'rate-limited'
  | 'bad-request'
  | 'server-error';

/** Hard cap on concurrent participants per room. */
export const MAX_PARTICIPANTS = 16;

/** Room codes use Crockford base32 (no I, L, O, U — unambiguous when read aloud). */
export const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const CODE_LENGTH = 16; // 16 * 5 = 80 bits of entropy
export const CODE_GROUP = 4;

/** `ABCD2EFG...` -> `ABCD-2EFG-...` for display. */
export function formatCode(code: RoomCode): string {
  return (code.match(new RegExp(`.{1,${CODE_GROUP}}`, 'g')) ?? []).join('-');
}

/**
 * Accept anything a human might paste — separators, lowercase, and the
 * Crockford-standard confusables (I/L -> 1, O -> 0) — and return the canonical
 * form, or null when it cannot be a valid code.
 */
export function normalizeCode(input: string): RoomCode | null {
  const cleaned = input
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0');
  if (cleaned.length !== CODE_LENGTH) return null;
  for (const ch of cleaned) if (!CODE_ALPHABET.includes(ch)) return null;
  return cleaned;
}
